

const formatTime = (seconds: number) => {
    const mins = Math.floor(seconds / 60);
    const secs = Math.floor(seconds % 60);
    return `${mins}:${secs < 10 ? '0' : ''}${secs}`;
};









import React, { useRef, useEffect, useState, useMemo } from 'react';
import { IconButton, Tooltip } from '@mui/material';
import OverSpeeding from 'packages/novous_web/src/assets/icons/OverSpeeding.svg';
import newPlay from 'packages/novous_web/src/assets/icons/newPlay.svg'
import pause from 'packages/novous_web/src/assets/icons/pause.svg'
import fullScreenIcon from 'packages/novous_web/src/assets/icons/full-screen-icon.svg'
import Collapse from 'packages/novous_web/src/assets/icons/Collapse.svg'
import downloadWhiteIcon from 'packages/novous_web/src/assets/icons/downloadWhiteIcon.svg'

type VideoPlayerProps = {
    availableHeight: number;
    availableWidth: number;
    key?: number;
    streamUrl: string;
    alertData?: any;
};


interface AlertData {
    gForce?: number[];
}

const AlertVideoPlayer: React.FC<VideoPlayerProps> = ({
    availableHeight,
    availableWidth,
    streamUrl,
    alertData = null,
}) => {
    const playerRef = useRef<HTMLVideoElement>(null);
    const containerRef = useRef<HTMLDivElement>(null);
    const heightWidth = useRef({ availableHeight, availableWidth });
    
    const [duration, setDuration] = useState(0);
    const [currentTime, setCurrentTime] = useState(0);
    const [alertTime, setAlertTime] = useState<number[]>([]);
    const [finalWidth, setFinalWidth] = useState(0);
    const [finalHeight, setFinalHeight] = useState(0);
    const [showTooltip, setShowTooltip] = useState(false);
    const [tooltipShown, setTooltipShown] = useState(false);
    const [isPlaying, setIsPlaying] = useState(true);
    const [isFullscreen, setIsFullscreen] = useState(false);

    const aspectRatio = 16 / 9;

    // Calculate width and height based on aspect ratio
    const updateVideoDimensions = () => {
        const heightBasedWidth = heightWidth.current.availableHeight * aspectRatio;
        const widthBasedHeight = heightWidth.current.availableWidth / aspectRatio;

        if (heightBasedWidth <= heightWidth.current.availableWidth) {
            setFinalWidth(heightBasedWidth);
            setFinalHeight(heightWidth.current.availableHeight);
        } else {
            setFinalWidth(heightWidth.current.availableWidth);
            setFinalHeight(widthBasedHeight);
        }
    };

    useEffect(() => {
        heightWidth.current = { availableHeight, availableWidth };
        updateVideoDimensions();
    }, [availableHeight, availableWidth]);

    useEffect(() => {
        const player = playerRef.current;
        if (!player) return;

        const handleLoadedMetadata = () => setDuration(player.duration);
        player.addEventListener('loadedmetadata', handleLoadedMetadata);
        player.style.objectFit = 'contain';

        return () => {
            player.removeEventListener('loadedmetadata', handleLoadedMetadata);
        };
    }, []);

    useEffect(() => {
        if (duration > 0) {
            setAlertTime([Math.floor(duration * 0.7)]);
        }
    }, [duration]);

    useEffect(() => {
        const handleFullscreenResize = () => {
            if (containerRef.current) {
                const { clientWidth, clientHeight } = containerRef.current;
                setFinalWidth(clientWidth);
                setFinalHeight(clientHeight);
            }
            setIsFullscreen(!!document.fullscreenElement);
        };

        const handleResize = () => {
            updateVideoDimensions();
        };

        const handleKeyDown = (e: KeyboardEvent) => {
            if (e.key === 'Escape') {
                setIsFullscreen(false);
            }
        };

        document.addEventListener('fullscreenchange', handleFullscreenResize);
        window.addEventListener('resize', handleResize);
        document.addEventListener('keydown', handleKeyDown);

        return () => {
            document.removeEventListener('fullscreenchange', handleFullscreenResize);
            window.removeEventListener('resize', handleResize);
            document.removeEventListener('keydown', handleKeyDown);
        };
    }, []);

    const togglePlayPause = () => {
        const player = playerRef.current;
        if (!player) return;

        if (player.paused) {
            player.play();
            setIsPlaying(true);
        } else {
            player.pause();
            setIsPlaying(false);
        }
    };

    const toggleFullscreen = () => {
        const elem = containerRef.current;
        if (!document.fullscreenElement && elem?.requestFullscreen) {
            elem.requestFullscreen();
        } else {
            document.exitFullscreen();
        }
    };

    const handleProgressChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        const newTime = (parseFloat(e.target.value) / 100) * duration;
        if (playerRef.current) {
            playerRef.current.currentTime = newTime;
            setCurrentTime(newTime);
        }
    };

    const downloadVideo = async (videoUrl: string) => {
        try {
            const response = await fetch(videoUrl);
            if (!response.ok) throw new Error("Failed to fetch video");

            const blob = await response.blob();
            const blobUrl = URL.createObjectURL(blob);

            const a = document.createElement('a');
            a.href = blobUrl;
            a.download = "video.mp4";
            document.body.appendChild(a);
            a.click();
            document.body.removeChild(a);
            URL.revokeObjectURL(blobUrl);
        } catch (error) {
            console.error("Error downloading video:", error);
        }
    };

    useEffect(() => {
        const alertSecond = Math.floor(duration * 0.7);
        const shouldShow = currentTime >= alertSecond && currentTime <= alertSecond + 1;
        setShowTooltip(shouldShow);
        setTooltipShown(shouldShow);
        if (currentTime === duration && duration > 0) {
            setIsPlaying(false);
        }
    }, [currentTime, duration]);

    return (
        <div className="flex items-center justify-center text-white" style={{ width: '100%', height: availableHeight }}>
            <div
                ref={containerRef}
                style={{
                    width: isFullscreen ? '100%' : `${finalWidth}px`,
                    height: isFullscreen ? '100%' : `${finalHeight}px`,
                    position: 'relative',
                    backgroundColor: '#000',
                }}
            >
                <video
                    ref={playerRef}
                    src={streamUrl}
                    muted
                    autoPlay
                    onTimeUpdate={() => setCurrentTime(playerRef.current?.currentTime || 0)}
                    style={{
                        width: `${finalWidth}px`,
                        height: `${finalHeight}px`,
                        objectFit: 'contain',
                        background: 'black',
                    }}
                />

                {duration > 0 && (
                    <div
                        style={{
                            position: 'absolute',
                            bottom: 0,
                            left: 0,
                            width: '100%',
                            background: 'rgba(0, 0, 0, 0.6)',
                            padding: '8px',
                            display: 'flex',
                            alignItems: 'center',
                            gap: '10px',
                            zIndex: 999,
                        }}
                    >
                        <button onClick={togglePlayPause} style={buttonStyle}>
                            <img
                                src={isPlaying ? pause : newPlay}
                                alt={isPlaying ? 'Pause' : 'Play'}
                                width={16}
                                height={16}
                                style={{ filter: "brightness(0) invert(1)" }}
                            />
                        </button>

                        <span style={{ minWidth: 60, textAlign: 'center' }}>
                            {formatTime(currentTime)} / {formatTime(duration)}
                        </span>

                        {/* Seek Bar */}
                        <div style={{ position: 'relative', flex: 1 }}>
                            <input
                                type="range"
                                min={0}
                                max={100}
                                value={(currentTime / duration) * 100 || 0}
                                onChange={handleProgressChange}
                                style={rangeStyle}
                            />
                            {alertData?.gForce?.length > 0 &&
                                alertTime.map((second, idx) => {
                                    const left = (second / duration) * 100;
                                    const markerWidth = Math.max(finalWidth / 15, 1);
                                    return (
                                        <Tooltip
                                            key={idx}
                                            title={<GForceChart gforce={alertData.gForce} showTooltip={showTooltip} />}
                                            open={true}
                                            arrow
                                            placement="top"
                                            slotProps={{
                                                popper: {
                                                    disablePortal: isFullscreen,
                                                    container: isFullscreen ? containerRef.current : undefined,
                                                    style: {
                                                        zIndex: 1300
                                                    }
                                                },
                                            }}
                                        >
                                            <div
                                                style={{
                                                    position: 'absolute',
                                                    left: `${left}%`,
                                                    top: '11px',
                                                    width: `${markerWidth}px`,
                                                    height: '7px',
                                                    backgroundColor: 'red',
                                                    pointerEvents: 'none',
                                                    borderRadius: '5px',
                                                    cursor: 'pointer',
                                                }}
                                            />
                                        </Tooltip>
                                    );
                                })}
                        </div>

                        <button onClick={toggleFullscreen} style={buttonStyle}>
                            <img
                                src={isFullscreen ? Collapse : fullScreenIcon}
                                alt="Fullscreen toggle"
                                width={20}
                                height={20}
                                style={{ filter: "brightness(0) invert(1)" }}
                            />
                        </button>

                        <button onClick={() => downloadVideo(streamUrl)} style={buttonStyle}>
                            <img src={downloadWhiteIcon} alt="Download" width={16} height={16} />
                        </button>
                    </div>
                )}
            </div>
        </div>
    );
};

const buttonStyle = {
    padding: '2px 2px',
    // fontSize: '16px',
    // background: '#ddd',
    // border: 'none',
    // borderRadius: '4px',
    cursor: 'pointer',
};

const rangeStyle = {
    width: '100%',
    height: '6px',
    borderRadius: '5px',
    appearance: 'none' as 'none', // Correcting the appearance property
    background: '#ccc',
};


export default AlertVideoPlayer;













import {
    LineChart,
    Line,
    XAxis,
    YAxis,
    CartesianGrid,
    ResponsiveContainer,
} from "recharts";


export const GForceChart = ({ gforce, showTooltip }: any) => {
    const [gForceData, setGForceData] = useState([]);
    useEffect(() => {
        if (gforce?.length > 0) {
            const data = gforce?.map((item: number, index: number) => ({
                time: `T${index + 1}`,
                gForce: item,
            }));
            setGForceData(data);
        }
    }, [gforce])





    return (
        <div style={{ backgroundColor: 'rgba(255, 255, 255, 0.75)' }}>

            {showTooltip ?
                <IconButton key="withoutanimation"
                    sx={
                        (theme) => ({
                            background: theme.palette.selected,
                            padding: '4px',
                            borderRadius: '8px',
                            position: 'absolute',
                            top: 18,
                            left: '40%',
                            zIndex: 999,
                            animation: 'blink 0.5s ease-in-out 3',
                            '@keyframes blink': {
                                '0%': { opacity: 1 },
                                '50%': { opacity: 0 },
                                '100%': { opacity: 1 },
                            },
                        })
                    }
                >
                    <img key="withoutanimation" alt="withoutanimation" src={OverSpeeding} width={24} height={24} />
                </IconButton>
                :
                <IconButton key="withoutanimation"
                    sx={
                        (theme) => ({
                            background: theme.palette.selected,
                            padding: '4px',
                            borderRadius: '8px',
                            position: 'absolute',
                            top: 18,
                            left: '40%',
                            zIndex: 999,
                        })
                    }
                >
                    <img key="withoutanimation" alt="withoutanimation" src={OverSpeeding} width={24} height={24} />
                </IconButton>
            }





            < ResponsiveContainer width={140} height={60}  >
                <LineChart
                    data={gForceData}
                    margin={{ top: 0, right: 0, left: 0, bottom: 0 }}
                >
                    {/* Grid removed */}
                    < XAxis dataKey="time" hide />
                    <YAxis hide />
                    <Line
                        type="monotone"
                        dataKey="gForce"
                        stroke="#C83333"
                        dot={false}
                        strokeWidth={2}
                    />
                </LineChart>
            </ResponsiveContainer>
        </div>
    );
};



















